import React, { useState } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Textarea } from './ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Play, CheckCircle, X, Edit2, Save } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface UnfinishedRowContentProps {
  reasonText?: string;
  startedAt?: string;
  progress?: number; // 0-100 percentage
  currentPage?: number;
  totalPages?: number;
  onMarkAsReading?: () => void;
  onMarkAsRead?: () => void;
  onRemove?: () => void;
  onReasonUpdate?: (newReason: string) => void;
  loading?: boolean;
}

function relativeTime(dateString: string): string {
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  } catch {
    return 'some time ago';
  }
}

export function UnfinishedRowContent({
  reasonText,
  startedAt,
  progress = 0,
  currentPage,
  totalPages,
  onMarkAsReading,
  onMarkAsRead,
  onRemove,
  onReasonUpdate,
  loading = false
}: UnfinishedRowContentProps) {
  const hasPageTracking = currentPage !== undefined && totalPages !== undefined && totalPages > 0;
  const [isEditing, setIsEditing] = useState(false);
  const [editedReason, setEditedReason] = useState(reasonText || '');

  const handleSave = () => {
    if (onReasonUpdate) {
      onReasonUpdate(editedReason);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditedReason(reasonText || '');
    setIsEditing(false);
  };
  
  return (
    <>
      {/* Status and metadata row */}
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-800 border-orange-300">
          Did Not Finish
        </Badge>
        
        {startedAt && (
          <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">
            Started {relativeTime(startedAt)}
          </span>
        )}
      </div>

      {/* Progress bar showing how far they got */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Stopped at</span>
          {hasPageTracking ? (
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages} ({progress}%)
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">{progress}%</span>
          )}
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Reason for not finishing */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="font-medium text-muted-foreground text-sm">Reason:</span>
          {onReasonUpdate && !isEditing && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditing(true)}
              className="h-7 px-2"
            >
              <Edit2 className="w-3 h-3" />
            </Button>
          )}
        </div>
        
        {isEditing ? (
          <div className="space-y-2">
            <Textarea
              value={editedReason}
              onChange={(e) => setEditedReason(e.target.value)}
              placeholder="Why did you stop reading this book?"
              className="min-h-[80px]"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave}>
                <Save className="w-3 h-3 mr-1" />
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </div>
        ) : reasonText ? (
          <div className="text-sm sm:text-base leading-relaxed whitespace-pre-line break-words">
            {reasonText}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic">
            {onReasonUpdate ? "Click edit to add a reason" : "No reason provided"}
          </p>
        )}
      </div>

      {/* Action buttons */}
      <TooltipProvider>
        <div className="flex gap-2 mt-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="default"
                onClick={onMarkAsReading}
                disabled={loading}
                className="flex-1"
                aria-label="Resume Reading"
              >
                <Play className="w-4 h-4 sm:w-3 sm:h-3 sm:mr-1 flex-shrink-0" />
                <span className="hidden sm:inline">Resume Reading</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent className="sm:hidden">
              <p>Resume Reading</p>
            </TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                onClick={onMarkAsRead}
                disabled={loading}
                className="flex-1"
                aria-label="Mark as Read"
              >
                <CheckCircle className="w-4 h-4 sm:w-3 sm:h-3 sm:mr-1 flex-shrink-0" />
                <span className="hidden sm:inline">Mark as Read</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent className="sm:hidden">
              <p>Mark as Read</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={onRemove}
                disabled={loading}
                aria-label="Remove"
              >
                <X className="w-4 h-4 sm:w-3 sm:h-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="sm:hidden">
              <p>Remove</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    </>
  );
}

export default UnfinishedRowContent;
